import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { faCheck, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../ui/Button/Button';

import { ErrorResponse } from '../../custom-fetch';
import { Route } from '../../routes';
import { QueryKey } from '../../services/query-keys';
import { useUserService } from '../../services/users-service';
import ErrorsAlert from '../ErrorsAlert/ErrorsAlert';
import FormButtons from '../ui/FormButtons/FormButtons';
import { ECommonValue, EInputType, IInputField, IOption } from '../ui/Input/Input';
import { useInputs } from '../ui/Input/useInputs';
import IUser from '../../shared/IUser';
import AuditTrailList from '../AuditTrail/AuditTrailList';

interface IProps {
  id: string;
  user?: IUser;
  organizationId?: string;
}

enum EInputs {
  firstName = "firstName",
  lastName = "lastName",
  email = "email",
  username = "username",
  phoneNumber = "phoneNumber",
  password = "password",
  role = "role",
  allowLogin = "allowLogin",
  allowGoogleLogin = "allowGoogleLogin",
  allowMicrosoftLogin = "allowMicrosoftLogin",
  firebaseUserId = "firebaseUserId",
}

const ROLE_OPTIONS: IOption[] = [
  { value: 'AGENT', label: 'Agent user' },
  { value: 'SUPERADMIN', label: 'Super Admin' },
];



const UserEdit: React.FC<IProps> = ({ id, user, organizationId }) => {
  const isEdit = id !== "add";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { updateUser, saveUser, deleteUser, registerFirebaseUser } = useUserService();

  const {
    mutate: registerMutate,
    isPending: isRegistering,
    isError: isRegisterError,
    error: registerError,
  } = useMutation<IUser, ErrorResponse, string>({
    mutationFn: (id: string) => registerFirebaseUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.users] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.users, id] });
    },
  });

  const { mutate, isPending, isError, error } = useMutation<
    IUser,
    ErrorResponse,
    IUser
  >({
    mutationFn: (data: IUser) => isEdit ? updateUser(id, data) : saveUser(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.users] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.users, id] });
      const orgId = data?.organizationId || user?.organizationId || organizationId;
      if (orgId) {
        navigate(Route.organization(orgId));
      } else {
        navigate(Route.organizations);
      }
    },
  });

  const {
    mutate: deleteMutate,
    isPending: isDeleting,
    isError: isDeletingError,
    error: deletingError,
  } = useMutation<boolean, ErrorResponse>({
    mutationFn: () => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.users] });
      const orgId = user?.organizationId || organizationId;
      if (orgId) {
        navigate(Route.organization(orgId));
      } else {
        navigate(Route.organizations);
      }
    },
  });

  const [inputs, setInputs] = useState<IInputField>({
    [EInputs.firstName]: {
      type: EInputType.text,
      label: "First Name",
      value: "",
    },
    [EInputs.lastName]: {
      type: EInputType.text,
      label: "Last Name",
      value: "",
    },
    [EInputs.email]: {
      type: EInputType.email,
      label: "Email",
      value: "",
      validation: { required: true },
    },
    [EInputs.username]: {
      type: EInputType.text,
      label: "Username",
      value: "",
    },
    [EInputs.phoneNumber]: {
      type: EInputType.tel,
      label: "Phone Number",
      value: "",
    },
    [EInputs.password]: {
      type: EInputType.password,
      label: "Password",
      value: "",
      validation: { required: !isEdit },
    },
    [EInputs.role]: {
      type: EInputType.select,
      label: "Role",
      value: "AGENT",
      options: ROLE_OPTIONS,
      validation: { required: true },
    },
    [EInputs.allowLogin]: {
      type: EInputType.checkbox,
      label: "Allow Password Login",
      value: !isEdit ? [ECommonValue.YES] : [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.allowGoogleLogin]: {
      type: EInputType.checkbox,
      label: "Allow Google Login",
      value: [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.allowMicrosoftLogin]: {
      type: EInputType.checkbox,
      label: "Allow Microsoft Login",
      value: [],
      options: [{ value: ECommonValue.YES, label: "" }],
    },
    [EInputs.firebaseUserId]: {
      type: EInputType.text,
      label: "Firebase ID",
      value: "",
    },
  });



  const firebaseIdValue = inputs[EInputs.firebaseUserId]?.value;

  useEffect(() => {
    if (firebaseIdValue) {
      setInputs((prev) => {
        let changed = false;
        const next = { ...prev };

        if (!(next[EInputs.allowGoogleLogin]?.value as any[])?.includes(ECommonValue.YES)) {
          next[EInputs.allowGoogleLogin] = {
            ...next[EInputs.allowGoogleLogin],
            value: [ECommonValue.YES],
          };
          changed = true;
        }

        if (!(next[EInputs.allowMicrosoftLogin]?.value as any[])?.includes(ECommonValue.YES)) {
          next[EInputs.allowMicrosoftLogin] = {
            ...next[EInputs.allowMicrosoftLogin],
            value: [ECommonValue.YES],
          };
          changed = true;
        }

        if (next[EInputs.password]?.validation?.required !== false) {
           next[EInputs.password] = {
             ...next[EInputs.password],
             validation: { ...next[EInputs.password].validation, required: false },
           };
           changed = true;
        }

        return changed ? next : prev;
      });
    } else {
      setInputs((prev) => {
        let changed = false;
        const next = { ...prev };
        if (next[EInputs.password]?.validation?.required !== !isEdit) {
           next[EInputs.password] = {
             ...next[EInputs.password],
             validation: { ...next[EInputs.password].validation, required: !isEdit },
           };
           changed = true;
        }
        return changed ? next : prev;
      });
    }
  }, [firebaseIdValue, isEdit]);

  const formattedData = useMemo(() => {
    if (!user) return undefined;
    return {
      ...user,
      allowLogin: user.allowLogin ? [ECommonValue.YES] : [],
      allowGoogleLogin: user.allowGoogleLogin ? [ECommonValue.YES] : [],
      allowMicrosoftLogin: user.allowMicrosoftLogin ? [ECommonValue.YES] : [],
    };
  }, [user]);

  const { createInput, submit } = useInputs({
    data: formattedData,
    inputs,
    setInputs,
  });

  const canRegister = useMemo(() => {
    return !!(
      inputs[EInputs.firstName].value &&
      inputs[EInputs.lastName].value &&
      inputs[EInputs.email].value
    );
  }, [inputs]);

  const submitHandler = async () => {
    const data = await submit();
    if (data) {
      const formattedSubmitData: any = {
        ...data,
        organizationId: user?.organizationId || organizationId || undefined,
        allowLogin: !!(data.allowLogin && data.allowLogin.indexOf(ECommonValue.YES) !== -1),
        allowGoogleLogin: !!(data.allowGoogleLogin && data.allowGoogleLogin.indexOf(ECommonValue.YES) !== -1),
        allowMicrosoftLogin: !!(data.allowMicrosoftLogin && data.allowMicrosoftLogin.indexOf(ECommonValue.YES) !== -1),
      };
      mutate(formattedSubmitData as IUser);
    }
  };

  const registerHandler = () => {
    registerMutate(id);
  };

  return (
    <>
      {user?.organizationName && (
        <div className="mb-3">
          <strong>Organization:</strong> {user.organizationName}
        </div>
      )}
      {user?.firebaseUserId && (
        <div className="mb-3 text-success d-flex align-items-center">
          <FontAwesomeIcon icon={faCheck} className="me-2" />
          <strong>Firebase Linked Account</strong>
          <FontAwesomeIcon icon={faShieldAlt} className="ms-2" title="Firebase authentication active" />
        </div>
      )}
      {createInput(EInputs.firstName)}
      {createInput(EInputs.lastName)}
      {createInput(EInputs.email, { disabled: !!user?.firebaseUserId })}
      {createInput(EInputs.firebaseUserId, { disabled: isEdit })}
      {createInput(EInputs.username)}
      {createInput(EInputs.phoneNumber)}
      {!inputs[EInputs.firebaseUserId].value && createInput(EInputs.password)}
      {createInput(EInputs.role)}
      {createInput(EInputs.allowLogin)}
      {createInput(EInputs.allowGoogleLogin)}
      {createInput(EInputs.allowMicrosoftLogin)}
      
      {isError && error && <ErrorsAlert errors={error.messages} />}
      {isDeletingError && deletingError && <ErrorsAlert errors={deletingError.messages} />} 
      {isRegisterError && registerError && <ErrorsAlert errors={registerError.messages} />}
      
      {isEdit && !user?.firebaseUserId && (
        <div className="mb-3">
          <Button 
            onClick={registerHandler} 
            loading={isRegistering} 
            disabled={!canRegister}
          >
             Register in Firebase
          </Button>
          {!canRegister && (
             <div className="text-muted small mt-1">
               First name, last name and email are required for Firebase registration.
             </div>
          )}
        </div>
      )}
      
      <FormButtons
        onSubmit={submitHandler}
        isLoading={isPending}
        onDelete={isEdit ? () => deleteMutate() : undefined}
        isDeleting={isDeleting}
        deleteConfirmMessage="Are you sure you want to delete this user?"
      />

      {isEdit && (
        <AuditTrailList kind="User" targetId={id} />
      )}
    </>
  );
};

export default UserEdit;
